import React, {useCallback} from "react";
import ReadOnlyInputBasic from "../../../../../components/input/ReadOnlyInputBasic";
import SimmsInput from "../../../../../components/input/SimmsInput";
import SimmsTextArea from "../../../../../components/input/SimmsTextArea";
import ITER_SimmsInput from "../../../../../components/input/ITER_SimmsInput";
import ITER_SimmsDatePicker from "../../../../../components/input/ITER_SimmsDatePicker";
import ITER_SimmsTextArea from "../../../../../components/input/ITER_SimmsTextArea";
import Selector from "../../../../../components/selector/Selector";
import SearchSelector from "../../../../../components/searchSelector/SearchSelector";
import UserSelectionField from "../../../../../components/selection/UserSelectionField";
import SimmsSelect from "../../../../../components/select/SimmsSelect";
import SimmsDatePicker from "../../../../../components/datepicker/SimmsDatePicker";
import SimmsInputSearch from "../../../../../components/input/SimmsInputSearch";
import SimmsDrawerSearch from "../../../../../components/drawer/simms/SimmsDrawerSearch";
import {CodeSpecNum, UserType} from "../../../../../constant/CodeSpecNum";
import {checkDuplicate} from "../../../../../components/commonmethod/CommonMethod";
import {assetNameDuplicate} from "../../../../../api/asset/AssetService";
import {
    onCreateClickListener,
    onSubCreateClickListener,
    onSubCreateClickListenerId,
} from "../../../../../components/commonmethod/simmsDrawerMethod";
import {inputOnChange, subInputOnChange} from "../../../../../components/commonmethod/simmsInputMethod";
import {selectSubOnChange} from "../../../../../components/commonmethod/simmsSelectMethod";
import {dateOnChange, dateOnSubChange} from "../../../../../components/commonmethod/simmsDateMethod";
import {customStringType, formatAuditUser} from "./assetDetailKv";

export function useAssetCreateEdit({
    asset,
    setAsset,
    date,
    ccFromDB,
    csFromDB,
    fieldsData,
    groupSelect,
    codeUpdater,
    customCodeUpdater,
    isAdminUser,
    renewGroupSelect,
    handleCreateSelectBox,
}) {
    const renderCreatedAt = () => <ReadOnlyInputBasic value={date.from} type="date" />;
    const renderUpdatedAt = () => <ReadOnlyInputBasic value={date.to} />;
    const renderCreatedBy = () => (
        <ReadOnlyInputBasic target="createdBy" value={formatAuditUser(asset.createdBy)} />
    );
    const renderUpdatedBy = () => (
        <ReadOnlyInputBasic target="updatedBy" value={formatAuditUser(asset.updatedBy)} />
    );

    const renderAssetName = () => (
        <SimmsInput
            auth
            onChange={(v) => inputOnChange(setAsset, "assetName", v, true)}
            data={asset.assetName}
            duplicate={(v) => checkDuplicate("", v, assetNameDuplicate)}
        />
    );

    const renderEtc = () => (
        <SimmsTextArea
            auth
            onChange={(v) => inputOnChange(setAsset, "etc", v, true)}
            data={asset.etc}
            style={{width: "100%"}}
        />
    );

    const renderProject = () => (
        <Selector
            auth
            onChange={(v) => onCreateClickListener(setAsset, "project", asset.project, v)}
            data={asset.project}
            entity="project"
        >
            <SimmsInputSearch type="TEXT" placeHolder="시스템 명" />
        </Selector>
    );

    const renderTask = () => (
        <Selector
            auth
            onChange={(v) => onCreateClickListener(setAsset, "task", asset.task, v)}
            data={asset.task}
            entity="code"
            parentKey={CodeSpecNum.TaskGroupCode}
            recursive
            updater={codeUpdater}
        />
    );

    const renderGroup = () => (
        <Selector
            auth
            onChange={(v) => onCreateClickListener(setAsset, "group", asset.group, v)}
            data={asset.group}
            entity="code"
            parentKey={CodeSpecNum.AssetGroupCode}
            recursive
            updater={codeUpdater}
        />
    );

    const renderLocation = () => (
        <Selector
            auth
            onChange={(v) => onCreateClickListener(setAsset, "location", asset.location, v)}
            data={asset.location}
            entity="code"
            parentKey={CodeSpecNum.AssetLocation}
            recursive
            updater={codeUpdater}
        />
    );

    const renderSlaGroup = () => (
        <Selector
            auth
            onChange={(v) => onCreateClickListener(setAsset, "slaGroup", asset.slaGroup, v)}
            data={asset.slaGroup}
            entity="code"
            parentKey={CodeSpecNum.SlaGroupCode}
            recursive
            updater={codeUpdater}
        />
    );

    const renderDepartment = () => (
        <Selector
            auth
            onChange={(v) => onCreateClickListener(setAsset, "department", asset.department, v)}
            data={asset.department}
            entity="department"
            uniqueKey="department"
            recursive
            notInsert
        />
    );

    const renderCustomCode = (code, index) => (
        <Selector
            auth
            entity="customCode"
            parentKey={code.id}
            uniqueKey={`customCode_${code.id}`}
            onChange={(v) =>
                onSubCreateClickListener(
                    setAsset,
                    "assetCustomCodes",
                    asset.assetCustomCodes?.[index]?.value,
                    {
                        id: asset.id,
                        subId: code.id,
                        subName: "customCode",
                        fieldName: "value",
                        index,
                    },
                    v,
                )
            }
            data={asset.assetCustomCodes?.find((item) => item.customCode?.id === code.id)?.value}
            recursive
            updater={customCodeUpdater}
        />
    );

    const renderCustomString = (eachCode, index) => {
        const t = customStringType(eachCode.customType);
        const payload = {
            id: asset.id,
            subId: eachCode.id,
            subName: "code",
            fieldName: "value",
            index,
        };
        const commonProps = {
            auth: true,
            data: asset?.assetCustomStrings?.[index]?.value,
            onChange: (v) => subInputOnChange(setAsset, "assetCustomStrings", payload, v),
        };

        if (t === "DATE") {
            return <ITER_SimmsDatePicker {...commonProps} />;
        }
        if (t === "TEXT") return <ITER_SimmsTextArea {...commonProps} />;
        return <ITER_SimmsInput {...commonProps} />;
    };

    const renderOperator = () => (
        <UserSelectionField
            layout="inline"
            data={asset.operator}
            onRemove={(v) =>
                setAsset((prev) => ({
                    ...prev,
                    operator: (prev.operator ?? []).filter((o) => o.id !== v.id),
                }))
            }
        >
            <SearchSelector
                auth
                multiSelect
                variant="compact"
                entity="userAccount"
                uniqueKey={UserType.operators}
                data={asset.operator}
                onChange={(v) => {
                    if (v?.__multiFlat) setAsset((prev) => ({...prev, operator: v.__multiFlat}));
                }}
            >
                <SimmsInputSearch type="TEXT" placeHolder="사용자명" />
                <SimmsDrawerSearch showLabel title="부서" entity="department" target="department" recursive />
                <SimmsDrawerSearch showLabel title="업체" entity="company" target="company" />
            </SearchSelector>
        </UserSelectionField>
    );

    const renderHandler = () => (
        <UserSelectionField
            layout="inline"
            data={asset.handler}
            onRemove={(v) =>
                setAsset((prev) => ({
                    ...prev,
                    handler: (prev.handler ?? []).filter((h) => h.id !== v.id),
                }))
            }
        >
            <SearchSelector
                auth
                multiSelect
                variant="compact"
                entity="userAccount"
                uniqueKey={UserType.handlers}
                data={asset.handler}
                onChange={(v) => {
                    if (v?.__multiFlat) setAsset((prev) => ({...prev, handler: v.__multiFlat}));
                }}
            >
                <SimmsInputSearch type="TEXT" placeHolder="사용자명" />
                <SimmsDrawerSearch showLabel title="부서" entity="department" target="department" recursive />
                <SimmsDrawerSearch showLabel title="업체" entity="company" target="company" />
            </SearchSelector>
        </UserSelectionField>
    );

    const renderMaintenanceCompanies = () => (
        <div className="web-flex">
            <Selector
                auth
                title="1차유지보수업체"
                onChange={(v) => onCreateClickListener(setAsset, "mngPrimary", asset.mngPrimary, v)}
                data={asset.mngPrimary}
                entity="company"
            >
                <SimmsInputSearch type="TEXT" placeHolder="업체명" />
            </Selector>
            <Selector
                auth
                title="2차유지보수업체"
                onChange={(v) => onCreateClickListener(setAsset, "mngSecondary", asset.mngSecondary, v)}
                data={asset.mngSecondary}
                entity="company"
            >
                <SimmsInputSearch type="TEXT" placeHolder="업체명" />
            </Selector>
            <Selector
                auth
                title="3차유지보수업체"
                onChange={(v) => onCreateClickListener(setAsset, "mngTertiary", asset.mngTertiary, v)}
                data={asset.mngTertiary}
                entity="company"
            >
                <SimmsInputSearch type="TEXT" placeHolder="업체명" />
            </Selector>
        </div>
    );

    const renderImportPrice = () => (
        <SimmsInput auth onChange={(v) => inputOnChange(setAsset, "importPrice", v, true)} data={asset.importPrice} />
    );

    const renderImportDate = () => (
        <SimmsDatePicker onChange={(v) => dateOnChange(setAsset, "importDate", v, false)} data={asset.importDate} />
    );

    const renderEndOfService = () => (
        <SimmsDatePicker
            onChange={(v) => dateOnChange(setAsset, "endOfService", v, false)}
            data={asset.endOfService}
        />
    );

    const renderMngPrice = () => (
        <SimmsInput auth onChange={(v) => inputOnChange(setAsset, "mngPrice", v, true)} data={asset.mngPrice} />
    );

    const renderMngRate = () => (
        <SimmsInput auth onChange={(v) => inputOnChange(setAsset, "mngRate", v, true)} data={asset.mngRate} />
    );

    const renderAssetField = (field, index) => {
        const fieldPayload = {
            id: asset.id,
            subId: field.id,
            subName: "field",
            fieldName: "value",
            index,
        };
        const each = asset.assetFields?.[index];

        if (field.type === "INPUT") {
            return (
                <ITER_SimmsInput
                    auth
                    onChange={(v) => subInputOnChange(setAsset, "assetFields", fieldPayload, v)}
                    data={each?.value}
                />
            );
        }
        if (field.type === "USER") {
            const userData =
                each?.value != null ? {id: each.value, rawValue: "USER"} : undefined;
            return (
                <SearchSelector
                    auth
                    variant="compact"
                    entity="userAccount"
                    title={field.title}
                    uniqueKey={`USER_${field.id}`}
                    onChange={(v) =>
                        onSubCreateClickListenerId(
                            setAsset,
                            "assetFields",
                            asset.assetFields?.[index]?.value,
                            fieldPayload,
                            v,
                        )
                    }
                    data={userData}
                >
                    <SimmsInputSearch type="TEXT" placeHolder="사용자명" />
                    <SimmsDrawerSearch showLabel title="부서" entity="department" target="department" recursive />
                    <SimmsDrawerSearch showLabel title="업체" entity="company" target="company" />
                </SearchSelector>
            );
        }
        if (field.type === "DATE") {
            return (
                <SimmsDatePicker
                    onChange={(v) => dateOnSubChange(setAsset, "assetFields", fieldPayload, v, false)}
                    data={each?.value}
                />
            );
        }
        if (field.type === "TEXT") {
            return (
                <ITER_SimmsTextArea
                    auth
                    style={{minHeight: "100px"}}
                    onChange={(v) => subInputOnChange(setAsset, "assetFields", fieldPayload, v)}
                    data={each?.value}
                />
            );
        }
        if (field.type === "SELECT" && groupSelect.find((e) => e.id === field.entity)?.children) {
            return (
                <SimmsSelect
                    onChange={(v) => selectSubOnChange(setAsset, "assetFields", fieldPayload, v)}
                    data={each?.value}
                    options={[
                        {id: null, name: "선택"},
                        ...(groupSelect.find((e) => e.id === field.entity)?.children),
                        ...(isAdminUser ? [{id: "추가", name: "추가"}] : []),
                    ]}
                    modal={{
                        title: field.title,
                        parent: field.entity,
                        refresh: renewGroupSelect,
                        create: handleCreateSelectBox,
                    }}
                />
            );
        }
        return null;
    };

    const renderersRef = React.useRef({});
    renderersRef.current = {
        createdAt: renderCreatedAt,
        updatedAt: renderUpdatedAt,
        createdBy: renderCreatedBy,
        updatedBy: renderUpdatedBy,
        assetName: renderAssetName,
        etc: renderEtc,
        project: renderProject,
        task: renderTask,
        group: renderGroup,
        location: renderLocation,
        slaGroup: renderSlaGroup,
        department: renderDepartment,
        customCode: renderCustomCode,
        customString: renderCustomString,
        operator: renderOperator,
        handler: renderHandler,
        maintenanceCompanies: renderMaintenanceCompanies,
        importPrice: renderImportPrice,
        importDate: renderImportDate,
        endOfService: renderEndOfService,
        mngPrice: renderMngPrice,
        mngRate: renderMngRate,
        assetField: renderAssetField,
    };

    const renderEdit = useCallback((key, ...args) => {
        const renderer = renderersRef.current[key];
        if (typeof renderer !== "function") return null;
        return args.length > 0 ? renderer(...args) : renderer();
    }, []);

    return {renderEdit};
}
